import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useAuth } from "../context/AuthContext";
import DashboardDemo from "../components/DashboardDemo";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { PLANS, getPlanPriceDisplay } from "../config/plans";
import SEO from "../components/SEO";
import { SITE_URL, DEFAULT_OG_IMAGE } from "../config/seo";
import { Sparkles, ArrowRight, Trophy, Star, Award, FileText, Users, Calendar, TrendingUp, Gift, Search, Box, Package, BadgePercent, ChevronDown } from 'lucide-react';

const IS_SAAS = process.env.REACT_APP_IS_SAAS === "true";

const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('public');

  const faqItems = t('faq.items', { returnObjects: true });
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Adisyon Takip',
      url: SITE_URL,
      logo: DEFAULT_OG_IMAGE,
      sameAs: [],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Adisyon Takip',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description: t('seo.description'),
      offers: {
        '@type': 'AggregateOffer',
        lowPrice: i18n.language === 'en' ? '19' : '750',
        highPrice: i18n.language === 'en' ? '69' : '2500',
        priceCurrency: i18n.language === 'en' ? 'USD' : 'TRY',
        offerCount: '3',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: Array.isArray(faqItems)
        ? faqItems.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a },
          }))
        : [],
    },
  ];

  const goToApp = () => {
    navigate("/home");
  };

  return (
    <div className="min-h-screen">
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        canonical={SITE_URL}
        jsonLd={jsonLd}
      />
      {/* ─── Hero ─── */}
      <div className="relative bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 overflow-hidden">
        {/* Decorative glows */}
        <div className="absolute top-0 right-0 w-[700px] h-[600px] bg-blue-600 rounded-full filter blur-[160px] opacity-10 pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-[500px] h-[400px] bg-cyan-500 rounded-full filter blur-[120px] opacity-10 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Navbar */}
          <div className="flex justify-between items-center py-5 border-b border-white/10">
            <span className="text-xl font-bold text-white">
              {t('landing.brand')}
            </span>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              {user ? (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-300 hidden sm:block">
                    {t('landing.welcome', { name: user.name || user.email })}
                  </span>
                  <button
                    onClick={goToApp}
                    className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-lg text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 transition-all"
                  >
                    {t('landing.goToApp')}
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Link
                    to="/login"
                    className="px-4 py-2 text-sm font-medium rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
                  >
                    {t('landing.login')}
                  </Link>
                  {IS_SAAS && (
                    <Link
                      to="/register"
                      className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-lg text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 transition-all shadow-lg shadow-blue-500/20"
                    >
                      {t('landing.register')}
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Hero Content */}
          <div className="pt-16 pb-28 text-center">
            {/* Announcement Pill */}
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-400/30 rounded-full px-4 py-1.5 text-sm text-blue-300 mb-10">
              <Sparkles className="h-3.5 w-3.5 flex-shrink-0" />
              {t('landing.hero.announcement')}
            </div>

            {/* Title */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight text-white leading-[1.1]">
              <span className="block">{t('landing.hero.titleLine1')}</span>
              <span className="block bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent pb-2">
                {t('landing.hero.titleLine2')}
              </span>
            </h1>

            {/* Subtitle */}
            <p className="mt-6 max-w-2xl mx-auto text-lg sm:text-xl text-slate-300 leading-relaxed">
              {t('landing.hero.subtitle')}
            </p>

            {/* CTA Buttons */}
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              {user ? (
                <button
                  onClick={goToApp}
                  className="inline-flex items-center px-8 py-3.5 text-base font-semibold rounded-xl text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 shadow-lg shadow-blue-500/25 transition-all duration-200"
                >
                  {t('landing.goToApp')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              ) : (
                <Link
                  to={IS_SAAS ? "/register" : "/login"}
                  className="inline-flex items-center px-8 py-3.5 text-base font-semibold rounded-xl text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 shadow-lg shadow-blue-500/25 transition-all duration-200"
                >
                  {t('landing.cta.start')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              )}
              <a
                href="#pricing"
                className="inline-flex items-center px-8 py-3.5 text-base font-semibold rounded-xl text-slate-200 border border-white/20 hover:bg-white/10 transition-colors"
              >
                {t('landing.cta.pricing')}
              </a>
            </div>

            {/* Stats Row */}
            <div className="mt-16 flex flex-wrap justify-center gap-x-12 gap-y-6 border-y border-white/10 py-8">
              {[
                { value: '30', label: t('landing.hero.stat.freeTrial') },
                { value: '1.500+', label: t('landing.hero.stat.businesses') },
                { value: '99.9%', label: t('landing.hero.stat.uptime') },
              ].map(({ value, label }) => (
                <div key={label} className="text-center min-w-[80px]">
                  <div className="text-3xl font-extrabold text-white">{value}</div>
                  <div className="text-sm text-slate-400 mt-1">{label}</div>
                </div>
              ))}
            </div>

            {/* Award Badges */}
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <div className="flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-2xl px-5 py-3">
                <Trophy className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                <span className="text-sm text-slate-200">{t('landing.badges.award2023')}</span>
              </div>
              <div className="flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-2xl px-5 py-3">
                <Star className="h-5 w-5 text-blue-400 flex-shrink-0" />
                <span className="text-sm text-slate-200">{t('landing.badges.bestUX')}</span>
              </div>
              <div className="flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-2xl px-5 py-3">
                <Award className="h-5 w-5 text-purple-400 flex-shrink-0" />
                <span className="text-sm text-slate-200">{t('landing.badges.award2022')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* ─── Demo ─── */}
      <div className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <span className="inline-block bg-cyan-50 text-cyan-600 text-sm font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-4">
              {t('landing.demo.eyebrow')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
              {t('landing.demo.title')}
            </h2>
            <p className="mt-4 max-w-2xl mx-auto text-lg text-gray-500">
              {t('landing.demo.subtitle')}
            </p>
          </div>

          {/* Browser Mockup Frame */}
          <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200 ring-1 ring-black/5">
            {/* Browser chrome bar */}
            <div className="flex items-center gap-3 bg-gray-800 px-4 py-3">
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
              </div>
              <div className="flex-1 mx-2">
                <div className="bg-gray-700 rounded-md px-3 py-1 flex items-center gap-2 max-w-xs mx-auto">
                  <div className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
                  <span className="text-xs text-gray-400 font-mono truncate">adisyon.app/dashboard</span>
                </div>
              </div>
            </div>
            {/* Demo content */}
            <div className="bg-white">
              <DashboardDemo />
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="mt-12 text-center">
            <p className="text-sm text-gray-400 mb-6">{t('landing.demo.disclaimer')}</p>
            {user ? (
              <button
                onClick={goToApp}
                className="inline-flex items-center px-8 py-3.5 text-base font-semibold rounded-xl text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 shadow-lg shadow-blue-500/20 transition-all duration-200"
              >
                {t('landing.goToApp')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            ) : (
              <Link
                to={IS_SAAS ? "/register" : "/login"}
                className="inline-flex items-center px-8 py-3.5 text-base font-semibold rounded-xl text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 shadow-lg shadow-blue-500/20 transition-all duration-200"
              >
                {t('landing.cta.tryFree')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            )}
          </div>
        </div>
      </div>
      
      {/* ─── Features ─── */}
      <div className="py-24 bg-white" id="features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="inline-block bg-blue-50 text-blue-600 text-sm font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-4">
              {t('landing.features.eyebrow')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
              {t('landing.features.title')}
            </h2>
            <p className="mt-4 max-w-2xl mx-auto text-lg text-gray-500">
              {t('landing.features.subtitle')}
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { Icon: FileText,     bg: 'bg-blue-50',    ic: 'text-blue-600',    tKey: 'bills' },
              { Icon: Users,       bg: 'bg-cyan-50',    ic: 'text-cyan-600',    tKey: 'customerTracking' },
              { Icon: Calendar,    bg: 'bg-indigo-50',  ic: 'text-indigo-600',  tKey: 'appointments' },
              { Icon: TrendingUp,  bg: 'bg-emerald-50', ic: 'text-emerald-600', tKey: 'reports' },
              { Icon: Gift,        bg: 'bg-rose-50',    ic: 'text-rose-600',    tKey: 'loyalty' },
              { Icon: Search,      bg: 'bg-purple-50',  ic: 'text-purple-600',  tKey: 'tracking' },
              { Icon: Box,         bg: 'bg-orange-50',  ic: 'text-orange-600',  tKey: 'stock' },
              { Icon: Package,     bg: 'bg-teal-50',    ic: 'text-teal-600',    tKey: 'packages' },
              { Icon: BadgePercent,bg: 'bg-violet-50',  ic: 'text-violet-600',  tKey: 'commissions' },
            ].map(({ Icon, bg, ic, tKey }) => (
              <div
                key={tKey}
                className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
              >
                <div className={`w-12 h-12 ${bg} rounded-xl flex items-center justify-center mb-4`}>
                  <Icon className={`h-6 w-6 ${ic}`} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {t(`landing.features.${tKey}.title`)}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {t(`landing.features.${tKey}.desc`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Fiyatlandırma */}
      <PricingSection />

      {/* SSS */}
      <FAQSection />

      <footer className="bg-gray-50">
        <div className="max-w-7xl mx-auto py-12 px-4 overflow-hidden sm:px-6 lg:px-8">
          <p className="mt-8 text-center text-base text-gray-500">
            {t('landing.footer', { year: new Date().getFullYear() })}
          </p>
        </div>
      </footer>
    </div>
  );
};

const FAQSection = () => {
  const { t } = useTranslation('public');
  const [openIndex, setOpenIndex] = useState(null);
  const items = t('faq.items', { returnObjects: true });
  if (!Array.isArray(items)) return null;

  return (
    <div className="py-20 bg-white" id="faq">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-block bg-blue-50 text-blue-600 text-sm font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-4">
            {t('faq.eyebrow')}
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
            {t('faq.title')}
          </h2>
        </div>
        <div className="divide-y divide-gray-200 border border-gray-200 rounded-2xl overflow-hidden">
          {items.map((item, i) => (
            <div key={i} className="bg-white">
              <button
                className="flex w-full items-center justify-between text-left px-6 py-5 hover:bg-gray-50 transition-colors"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <span className="text-base font-semibold text-gray-900 pr-4">{item.q}</span>
                <ChevronDown
                  className={`h-5 w-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${openIndex === i ? 'rotate-180' : ''}`}
                />
              </button>
              {openIndex === i && (
                <div className="px-6 pb-5">
                  <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PricingSection = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const { t, i18n } = useTranslation('public');
  const isYearly = billingCycle === 'yearly';

  const FEATURE_ROWS = [
    { labelKey: 'pricing.features.billManagement',     plans: ['starter', 'pro', 'premium'] },
    { labelKey: 'pricing.features.customerManagement', plans: ['starter', 'pro', 'premium'] },
    { labelKey: 'pricing.features.staffList',          plans: ['starter', 'pro', 'premium'] },
    { labelKey: 'pricing.features.basicReports',       plans: ['starter', 'pro', 'premium'] },
    { labelKey: 'pricing.features.maxThreeStaff',      plans: ['starter'] },
    { labelKey: 'pricing.features.unlimitedStaff',        plans: ['pro', 'premium'] },
    { labelKey: 'pricing.features.basicAppointments',     plans: ['starter', 'pro', 'premium'] },
    { labelKey: 'pricing.features.advancedAppointments',  plans: ['pro', 'premium'] },
    { labelKey: 'pricing.features.loyalty',               plans: ['pro', 'premium'] },
    { labelKey: 'pricing.features.advancedTracking',    plans: ['pro', 'premium'] },
    { labelKey: 'pricing.features.packageSessions',    plans: ['pro', 'premium'] },
    { labelKey: 'pricing.features.stockManagement',    plans: ['pro', 'premium'] },
    { labelKey: 'pricing.features.commissions',        plans: ['pro', 'premium'] },
    { labelKey: 'pricing.features.smsReminders',       plans: ['pro', 'premium'], comingSoon: true },
    { labelKey: 'pricing.features.customDomainBooking',plans: ['premium'] },
    { labelKey: 'pricing.features.customDomainApp',    plans: ['premium'] },
    { labelKey: 'pricing.features.whatsapp',            plans: ['premium'], comingSoon: true },
    { labelKey: 'pricing.features.multiBranch',        plans: ['premium'], comingSoon: true },
    { labelKey: 'pricing.features.prioritySupport',    plans: ['premium'] },
    { labelKey: 'pricing.features.aiCallCenter',       plans: ['premium'], comingSoon: true, infoKey: 'pricing.features.aiCallCenterInfo' },
  ];

  const CHECK = () => (
    <svg className="h-4 w-4 text-green-500 mx-auto" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  );
  const DASH = () => <span className="text-gray-300 block text-center">—</span>;
  const SOON = () => (
    <span className="flex justify-center">
      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 leading-none">
        Yakında
      </span>
    </span>
  );

  return (
    <div className="py-20 bg-gray-50" id="pricing">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Başlık */}
        <div className="text-center mb-10">
          <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">{t('pricing.eyebrow')}</h2>
          <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
            {t('pricing.title')}
          </p>
          <p className="mt-4 max-w-xl mx-auto text-lg text-gray-500">
            {t('pricing.subtitle')}
          </p>
        </div>

        {/* Toggle */}
        <div className="flex items-center justify-center gap-3 mb-10">
          <span className={`text-sm font-medium ${!isYearly ? 'text-gray-900' : 'text-gray-400'}`}>{t('pricing.monthly')}</span>
          <button
            onClick={() => setBillingCycle(isYearly ? 'monthly' : 'yearly')}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              isYearly ? 'bg-blue-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                isYearly ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <span className={`text-sm font-medium ${isYearly ? 'text-gray-900' : 'text-gray-400'}`}>
            {t('pricing.yearly')}
          </span>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold transition-colors ${
            isYearly ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
          }`}>
            {t('pricing.savingBadge')}
          </span>
        </div>

        {/* Plan Kartları */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {PLANS.map((plan) => {
            const priceDisplay = getPlanPriceDisplay(plan.id, billingCycle, i18n.language);
            const isPopular = plan.popular;
            const isPremium = plan.id === 'premium';
            const highlights = t(`plans.${plan.id}.highlights`, { returnObjects: true });
            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl border-2 p-6 flex flex-col bg-white shadow-sm ${
                  isPremium
                    ? 'border-purple-300'
                    : isPopular
                    ? 'border-blue-400 ring-2 ring-blue-100'
                    : 'border-gray-200'
                }`}
              >
                {isPopular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center px-3 py-0.5 rounded-full text-xs font-bold bg-blue-600 text-white shadow">
                    {t('pricing.mostPopular')}
                  </span>
                )}
                {isPremium && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center px-3 py-0.5 rounded-full text-xs font-bold bg-purple-600 text-white shadow">
                    {t('pricing.premiumBadge')}
                  </span>
                )}

                <div className="mb-4">
                  <h3 className="text-lg font-bold text-gray-900">{t(`plans.${plan.id}.name`)}</h3>
                  <p className="text-sm text-gray-500 mt-0.5">{t(`plans.${plan.id}.description`)}</p>
                </div>

                <div className="mb-5">
                  <span className="text-4xl font-extrabold text-gray-900">
                    {priceDisplay.symbolBefore ? `${priceDisplay.symbol}${priceDisplay.formatted}` : priceDisplay.formatted}
                  </span>
                  <span className="text-gray-500 text-sm ml-1">
                    {priceDisplay.symbolBefore ? '/' : `${priceDisplay.symbol}/`}{isYearly ? t('pricing.perYear') : t('pricing.perMonth')}
                  </span>
                  {isYearly && (
                    <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-gray-400 line-through">
                        {priceDisplay.symbolBefore
                          ? `${priceDisplay.symbol}${priceDisplay.baseMonthlyFormatted}`
                          : `${priceDisplay.baseMonthlyFormatted} ${priceDisplay.symbol}`
                        }/{t('pricing.perMonth')}
                      </span>
                      <span className="text-sm text-green-600 font-medium">
                        {priceDisplay.symbolBefore
                          ? `${priceDisplay.symbol}${priceDisplay.yearlyMonthlyEquivFormatted}`
                          : `${priceDisplay.yearlyMonthlyEquivFormatted} ${priceDisplay.symbol}`
                        }/{t('pricing.perMonth')}
                      </span>
                    </div>
                  )}
                </div>

                <ul className="space-y-2 mb-6 flex-1">
                  {(Array.isArray(highlights) ? highlights : []).map((h) => {
                    const aiInfoMarker = /\s*\[ai-info\]\s*$/;
                    const comingSoonMarker = /\s*\((yakında|coming soon)\)\s*$/i;
                    const hasAiInfo = aiInfoMarker.test(h);
                    const hClean = h.replace(aiInfoMarker, '');
                    const isComingSoon = comingSoonMarker.test(hClean);
                    const label = hClean.replace(comingSoonMarker, '');
                    if (hasAiInfo) {
                      return (
                        <li key={h} className="rounded-xl bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200/80 p-3 -mx-1 mt-1">
                          <div className="flex items-start gap-2">
                            <Sparkles className="h-4 w-4 text-purple-500 flex-shrink-0 mt-0.5" />
                            <div className="flex flex-col gap-1 min-w-0">
                              <span className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-sm font-semibold text-purple-900">{label}</span>
                                {isComingSoon && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 leading-none">
                                    Yakında
                                  </span>
                                )}
                                <span className="relative group inline-flex">
                                  <button className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-purple-200 text-purple-700 hover:bg-purple-300 text-[9px] font-bold leading-none flex-shrink-0 cursor-pointer">
                                    i
                                  </button>
                                  <span className="absolute left-0 bottom-full mb-2 w-56 bg-gray-900 text-white text-xs rounded-lg px-3 py-2.5 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none shadow-xl leading-relaxed">
                                    {t('pricing.features.aiCallCenterInfo')}
                                    <span className="absolute top-full left-3 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900" />
                                  </span>
                                </span>
                              </span>
                              <span className="text-[11px] text-purple-500 font-medium">
                                {t('pricing.features.aiCallCenterTagline')}
                              </span>
                            </div>
                          </div>
                        </li>
                      );
                    }
                    return (
                    <li key={h} className="flex items-start gap-2 text-sm text-gray-600">
                      <svg className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="flex items-center gap-1.5 flex-wrap">
                        {label}
                        {isComingSoon && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 leading-none">
                            Yakında
                          </span>
                        )}
                      </span>
                    </li>
                  );
                  })}
                </ul>

                <Link
                  to="/register"
                  className={`w-full text-center py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                    isPremium
                      ? 'bg-purple-600 hover:bg-purple-700 text-white'
                      : isPopular
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'border-2 border-gray-300 hover:border-blue-400 text-gray-700 hover:text-blue-600'
                  }`}
                >
                  {t('pricing.freeTrial')}
                </Link>
              </div>
            );
          })}
        </div>

        {/* Özellik Karşılaştırma Tablosu */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-medium text-gray-500 w-2/5">{t('pricing.featureTableHeader')}</th>
                {PLANS.map((p) => (
                  <th key={p.id} className="text-center px-3 py-3 font-semibold text-gray-700">{t(`plans.${p.id}.name`)}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {FEATURE_ROWS.map(({ labelKey, plans: included, comingSoon, infoKey }) => (
                <tr key={labelKey} className="hover:bg-gray-50">
                  <td className="px-5 py-3 text-gray-600">
                    <span className="flex items-center gap-2 flex-wrap">
                      {t(labelKey)}
                      {comingSoon && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 leading-none">
                          Yakında
                        </span>
                      )}
                      {infoKey && (
                        <span className="relative group inline-flex">
                          <button className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 text-[10px] font-bold leading-none flex-shrink-0 cursor-pointer">
                            i
                          </button>
                          <span className="absolute left-0 bottom-full mb-2 w-64 bg-gray-900 text-white text-xs rounded-lg px-3 py-2.5 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none shadow-xl leading-relaxed">
                            {t(infoKey)}
                            <span className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900" />
                          </span>
                        </span>
                      )}
                    </span>
                  </td>
                  {PLANS.map((p) => (
                    <td key={p.id} className="px-3 py-3">
                      {included.includes(p.id) ? (comingSoon ? <SOON /> : <CHECK />) : <DASH />}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Alt CTA */}
        <div className="text-center mt-10">
          <Link
            to="/register"
            className="inline-flex items-center px-8 py-3 border border-transparent text-base font-semibold rounded-xl shadow text-white bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 transition-colors"
          >
            {t('pricing.bottomCta')}
          </Link>
          <p className="mt-3 text-sm text-gray-400">{t('pricing.noCard')}</p>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
